import React, { useEffect, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import { organizationFlowObject } from '@/app.constant'
import { getAllUserGroupOptions } from '@/store/notifications/actions'
import { getStickerSets, updateOrganizationFlow } from '@/store/common/actions'
import { addEntityToKnowledgeGroup } from '@/store/knowledge/actions'
import {
  createIntent,
  getAllButtonOptions,
  getAllIntents,
  getAllUserGroups,
  getParaPhrasesBasedOnIntent,
  getParaPhrasesWithoutIntent,
  updateIntent,
} from '@/store/questions/actions'
import {
  AllUserGroups,
  getIntent,
  isQuestionLoading,
} from '@/store/questions/selector'
import Loading from '@/components/UI/Loading/Loading'
import ButtonsTab from './partials/ButtonsTab'
import SettingsTab from './partials/SettingsTab'
import UserSaysTab from './partials/UserSaysTab'
import VirtualAssistantTab from './partials/VirtualAssistantTab'
import ActionButtons from './partials/ActionButtons'
import GenerateVariationsSection from './partials/GenerateVariationsSection'
import {
  getKnowledgeById,
  localUserQuestionUpdate,
} from '@/store/questions/actions'

import ChildPageHeader from '@/components/UI/ChildPageHeader'
import { KNOWLEDGE_ALL } from '@/config'
import KGroupsTree from '@/components/UI/KGroupsTree'
import './KnowledgeCreateFromScratch.scss'

const KnowledgeCreateFromScratch = (props: any) => {
  const { t } = useTranslation('translation')
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const questionLoading = useSelector(isQuestionLoading)
  const allUserGroupsData = useSelector(AllUserGroups)
  const intent = useSelector(getIntent)
  const [isIntentUpdating, setIsIntentUpdating] = useState(false)
  const [buttonText, setButtonText] = useState('')
  const [activeTab, setActiveTab] = useState('usersays')
  const [selectedButtonTarget, setSelectedButtonTarget] = useState(null)
  const [targetButtonsList, setTargetOptionsList] = useState([])
  const [hasAddedQuestion, setHasAddedQuestion] = useState(false)
  const [userSaysTarget, setUserSaysTarget] = useState(null)
  const [hasAddedResponse, setHasAddedResponse] = useState(false)
  const [selectedResponseTarget, setSelectedResponseTarget] = useState(null)
  const [paraPhrasesLoading, setParaPhrasesLoading] = useState(false)

  const [generateVariations, setGenerateVariations] = useState(false)

  // Local selection for group id (replaces commonState selectedGroupId/selectedYear usage)
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)

  useEffect(() => {
    if (
      props.location &&
      props.location.state &&
      (props.location.state.que || props.location.state.ans)
    ) {
      if (props.location.state.que) {
        const oldList = []
        oldList.unshift({
          text: props.location.state.que,
        })
        dispatch(localUserQuestionUpdate('questions', oldList))
      }

      if (props.location.state.ans) {
        const oldResponses = []
        oldResponses.unshift({
          text: props.location.state.ans,
          group_ids: [],
        })
        dispatch(localUserQuestionUpdate('responses', oldResponses))
      }
    }
  }, [props.location])

  useEffect(() => {
    dispatch(
      updateOrganizationFlow({
        ...organizationFlowObject,
        prevRoute: '/knowledge',
        currentRoute: '/knowledge-create-from-scratch',
        nextRoute: '/knowledge/generate-questions',
        setup_completed: false,
      }),
    )

    dispatch(getAllUserGroups())
    dispatch(getStickerSets())
    dispatch(getAllUserGroupOptions()) // user groups

    setUserSaysTarget(null)
    setSelectedResponseTarget(null)
    setHasAddedQuestion(false)
    setHasAddedResponse(false)

    // Use selectedGroupId prop if provided (drawer mode), otherwise read from URL
    if (props.selectedGroupId) {
      dispatch(
        localUserQuestionUpdate('knowledge_group_id', props.selectedGroupId),
      )
      setGenerateVariations(true)
    } else {
      const query = new URLSearchParams(location.search)
      const group = query.get('group')
      const viewAll = query.get('view-all')
      setGenerateVariations(viewAll === 'true')
      if (group) {
        dispatch(localUserQuestionUpdate('knowledge_group_id', group))
      }
    }
    return () => {
      dispatch({
        type: 'FETCH_KNOWLEDGE_BY_ID_SUCCESS',
        payload: {
          intent: null,
        },
      })
    }
  }, [])

  useEffect(() => {
    // Check if intent is already present dispatch locally
    const getSelectedIntent = async (selectedIntent: any) => {
      if (selectedIntent && selectedIntent.hasOwnProperty('_id')) {
        await dispatch(getKnowledgeById(selectedIntent._id))
      }
    }

    getSelectedIntent(props.selectedIntent)
    // Fetch all button options in background
    dispatch(getAllButtonOptions(true))
    dispatch(getAllIntents(true))
  }, [props.selectedIntent])

  useEffect(() => {
    // When not in edit mode, keep knowledge_group_id synced with local selectedGroupId
    if (!props.editMode && selectedGroupId) {
      dispatch(localUserQuestionUpdate('knowledge_group_id', selectedGroupId))
    }
  }, [selectedGroupId, props.editMode])

  useEffect(() => {
    if (
      !questionLoading &&
      intent &&
      props.selectedIntent &&
      props.selectedIntent.existingRecord
    ) {
      const oldList = intent.questions ? [...intent.questions] : []
      oldList.unshift({ text: props.selectedIntent.existingRecord.question })
      dispatch(localUserQuestionUpdate('questions', oldList))

      const oldResponses = intent.responses ? [...intent.responses] : []
      oldResponses.unshift({
        text: props.selectedIntent.existingRecord.answer,
        group_ids: [],
      })
      dispatch(localUserQuestionUpdate('responses', oldResponses))
    }
  }, [questionLoading])

  //
  useEffect(() => {
    buttonTextHandler()
  }, [intent])

  // Create intent
  const createIntenthandler = async () => {
    // For creation
    if (
      !props.editMode &&
      activeTab === 'usersays' &&
      (!intent || !intent.questions || intent.questions.length === 0)
    ) {
      try {
        if (selectedButtonTarget) {
          const oldButtonsList = [...targetButtonsList]
          oldButtonsList.push({
            ...selectedButtonTarget,
            name: selectedButtonTarget.name,
            type: selectedButtonTarget.type,
          })
          await dispatch(localUserQuestionUpdate('buttons', oldButtonsList))
          setSelectedButtonTarget(null)
        }

        if (userSaysTarget) {
          const oldList = []
          await oldList.unshift(userSaysTarget)
          await dispatch(localUserQuestionUpdate('questions', oldList))
          setUserSaysTarget(null)
          if (!intent || !intent.responses) {
            setHasAddedResponse(true)
            tabsChangeHandler('virtualassistant')
            return
          }
        }

        if (selectedResponseTarget) {
          const oldList = []
          await oldList.unshift(selectedResponseTarget)
          await dispatch(localUserQuestionUpdate('responses', oldList))
        }

        await setHasAddedResponse(true)
        await setHasAddedQuestion(true)
        dispatch(createIntent())
      } catch (e) {}
      return
    }

    // If questions but no responses
    if (
      !props.editMode &&
      activeTab === 'usersays' &&
      intent &&
      intent.questions &&
      intent.questions.length > 0
    ) {
      tabsChangeHandler('virtualassistant')
      return
    }

    // All good proceed
    setIsIntentUpdating(true)
    try {
      if (selectedButtonTarget) {
        const oldButtonsList = [...targetButtonsList]
        oldButtonsList.push({
          ...selectedButtonTarget,
          name: selectedButtonTarget.name,
          type: selectedButtonTarget.type,
        })
        await dispatch(localUserQuestionUpdate('buttons', oldButtonsList))
      }

      if (
        userSaysTarget &&
        userSaysTarget.text &&
        userSaysTarget.text.trim().length > 0
      ) {
        let oldList = []
        oldList = intent && intent.questions ? [...intent.questions] : []
        await oldList.unshift(userSaysTarget)
        await dispatch(localUserQuestionUpdate('questions', oldList))
      }

      if (selectedResponseTarget) {
        const oldList = []
        await oldList.unshift(selectedResponseTarget)
        await dispatch(localUserQuestionUpdate('responses', oldList))
      }

      await setHasAddedResponse(true)
      await setHasAddedQuestion(true)
      dispatch(getAllButtonOptions(true))
      await dispatch(createIntent())
      history.push(`${KNOWLEDGE_ALL}?activeTab=viewKnowledge`)
    } catch (error: any) {}
    setIsIntentUpdating(false)
  }

  const isUserSaysActivityDetected = (e: any) => {
    setUserSaysTarget({
      text: e,
    })
  }

  const isResponseActivityDetected = (e: any) => {
    setSelectedResponseTarget(e)
  }

  const isActivityDetected = (e: any, buttonsList: any[]) => {
    setSelectedButtonTarget(e)
    setTargetOptionsList([...buttonsList])
  }

  const tabsChangeHandler = (e: any) => {
    setActiveTab(e)
    buttonTextHandler(e)
    if (props.editMode) {
      setUserSaysTarget(null)
      setSelectedResponseTarget(null)
    }
  }

  const buttonTextHandler = (tab = activeTab) => {
    if (!props.editMode) {
      if (tab === 'usersays') {
        setButtonText(t('Next'))
      } else if (tab === 'virtualassistant') {
        setButtonText(t('Save'))
      }
    }
  }

  const updateIntenthandler = async () => {
    if (activeTab === 'level') {
      if (selectedGroupId) {
        setIsIntentUpdating(true)
        dispatch(
          addEntityToKnowledgeGroup(selectedGroupId, 'intent', [intent._id]),
        )
        setIsIntentUpdating(false)
      }
    } else {
      setIsIntentUpdating(true)
      if (selectedButtonTarget) {
        const oldButtonsList = [...targetButtonsList]
        oldButtonsList.push({
          ...selectedButtonTarget,
          name: selectedButtonTarget.name,
          type: selectedButtonTarget.type,
        })
        await dispatch(localUserQuestionUpdate('buttons', oldButtonsList))
      }

      if (
        userSaysTarget &&
        userSaysTarget.text &&
        userSaysTarget.text.trim().length > 0
      ) {
        let oldList = []
        oldList = intent && intent.questions ? [...intent.questions] : []
        await oldList.unshift(userSaysTarget)
        await dispatch(localUserQuestionUpdate('questions', oldList))
      }

      if (selectedResponseTarget) {
        const oldList = [...intent.responses]
        await oldList.unshift(selectedResponseTarget)
        await dispatch(localUserQuestionUpdate('responses', oldList))
      }

      await dispatch(updateIntent())

      // Check if intent is already present dispatch locally
      const getSelectedIntent = async (selectedIntent: any) => {
        if (selectedIntent && selectedIntent.hasOwnProperty('_id')) {
          await dispatch(getKnowledgeById(selectedIntent._id))
        }
      }

      getSelectedIntent(props.selectedIntent)
      // Fetch all button options in background
      dispatch(getAllButtonOptions(true))
      dispatch(getAllIntents(true))
      resetEditKnowledgehandler()
      setIsIntentUpdating(false)
    }
  }

  const resetEditKnowledgehandler = () => {
    dispatch({
      type: 'FETCH_KNOWLEDGE_BY_ID_SUCCESS',
      payload: {
        intent: null,
      },
    })
    props.editMode ? props.resetEditKnowledgehandler() : history.goBack()
  }

  const getParaPhrasesBasedOnIntentHandler = async () => {
    try {
      setParaPhrasesLoading(true)
      const { questions, is_paraphrased }: any = await dispatch(
        getParaPhrasesBasedOnIntent(intent?._id),
      )
      setParaPhrasesLoading(false)

      let oldQuestions: any[] = []
      if (questions.length) {
        oldQuestions = [...questions]
      }
      await dispatch(localUserQuestionUpdate('questions', oldQuestions))
      await dispatch(localUserQuestionUpdate('is_paraphrased', is_paraphrased))
    } catch (error) {
      setParaPhrasesLoading(false)
      console.log('[ error ]', error)
    }
  }

  const getParaPhrasesWithoutIntentHandler = async () => {
    try {
      setParaPhrasesLoading(true)
      let oldList: any[] = []
      let list: any = []
      if (intent && intent.questions) {
        oldList = [...intent.questions]
      }
      if (userSaysTarget) {
        await oldList.unshift(userSaysTarget)
      }

      if (oldList && oldList.length > 0) {
        for (let index = 0; index < oldList.length; index++) {
          const element = oldList[index]
          if (element?.text?.trim().length > 0) {
            list.push(element.text)
          }
        }
      } else {
        return
      }

      const { paraphrased_questions }: any = await dispatch(
        getParaPhrasesWithoutIntent(list),
      )
      setParaPhrasesLoading(false)

      let oldQuestions: any[] = []
      if (paraphrased_questions && paraphrased_questions.length > 0) {
        oldQuestions = [...paraphrased_questions]
      }
      oldQuestions = oldQuestions.map((e: any) => {
        return { text: e }
      })

      await dispatch(localUserQuestionUpdate('questions', oldQuestions))
      await dispatch(localUserQuestionUpdate('is_paraphrased', true))
    } catch (error) {
      setParaPhrasesLoading(false)
      console.log('[ error ]', error)
    }
  }

  // questionLoading
  if (isIntentUpdating) {
    return <Loading />
  }

  return (
    <>
      <section className={props.hideHeader ? '' : 'page-mid-wraper pb-0'}>
        {!props.hideHeader && (
          <ChildPageHeader
            title={
              props.editMode
                ? intent && intent.questions && intent.questions.length > 0
                  ? intent.questions[0].text
                  : intent?.name
                : t('Create new Flash Card')
            }
            subTitle={
              props.editMode
                ? ''
                : t(
                    'Create a Flash Card by adding questions and answer variations manually.',
                  )
            }
            hideBackButton={!!props.editMode}
            redirectUrl={`/knowledge/all?activeTab=viewKnowledge`}
          />
        )}

        <div>
          <div className="KnowledgeCreateFromScratch">
            <Tabs
              defaultActiveKey="usersays"
              onSelect={tabsChangeHandler}
              activeKey={activeTab}
            >
              <Tab eventKey="usersays" title={t('Questions')}>
                <UserSaysTab
                  activeTab={activeTab}
                  editMode={props.editMode}
                  isActivityDetected={isUserSaysActivityDetected}
                  hasAdded={hasAddedQuestion}
                />
              </Tab>
              <Tab
                eventKey="virtualassistant"
                title={t('Answers')}
                disabled={
                  !props.editMode &&
                  intent &&
                  ((intent.questions && intent.questions.length === 0) ||
                    !intent.questions) &&
                  allUserGroupsData.length > 0
                }
              >
                <VirtualAssistantTab
                  allUserGroups={[...allUserGroupsData]}
                  isActivityDetected={isResponseActivityDetected}
                  hasAdded={hasAddedResponse}
                  nodeElement={true}
                />
              </Tab>
              <Tab eventKey="buttons" title={t('Buttons')}>
                <ButtonsTab
                  editMode={props.editMode}
                  isActivityDetected={isActivityDetected}
                />
              </Tab>
              <Tab eventKey="level" title={t('Group')}>
                <KGroupsTree
                  actions={{ add: true }}
                  syncsState={false}
                  onSelectGroup={(group: any) =>
                    setSelectedGroupId(group?._id || null)
                  }
                />
              </Tab>
              <Tab eventKey="settings" title={t('Settings')}>
                <SettingsTab
                  editMode={props.editMode}
                  showsParentAnswer={!!props?.selectedIntent?.parent_id}
                />
              </Tab>
            </Tabs>
          </div>
          {(props.viewall || generateVariations) && (
            <div className="d-flex align-items-center flex-wrap justify-content-end mb-2">
              <GenerateVariationsSection
                paraPhrasesLoading={paraPhrasesLoading}
                intent={intent}
                activeTab={activeTab}
                userSaysTarget={userSaysTarget}
                generateVariations={generateVariations}
                onGenerateWithIntent={getParaPhrasesBasedOnIntentHandler}
                onGenerateWithoutIntent={getParaPhrasesWithoutIntentHandler}
              />

              <ActionButtons
                editMode={props.editMode}
                buttonText={buttonText}
                onSave={updateIntenthandler}
                onCancel={resetEditKnowledgehandler}
                onCreate={createIntenthandler}
                className="ml-auto"
              />
            </div>
          )}
        </div>
      </section>
      {!props.viewall && !generateVariations && (
        <footer>
          <div>
            <ActionButtons
              editMode={props.editMode}
              buttonText={buttonText}
              onSave={updateIntenthandler}
              onCancel={resetEditKnowledgehandler}
              onCreate={createIntenthandler}
            />
          </div>
        </footer>
      )}
    </>
  )
}

export default KnowledgeCreateFromScratch
